
import Vue from 'vue';
import pluralize from 'pluralize';
import _mapKeys from 'lodash/mapKeys';
import { Runnit, CompanyCustomFieldDef, Team, TeamMember } from '@run-diffusion/shared';
import { mapState, mapActions } from 'vuex';
import { db } from '@/firebase';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { RunnitsCRUDMixin, TeamUserMixin } from '@/mixins';
import RunnitBoardRow from '@/views/Runnits/pages/RunnitBoardsPage/RunnitBoardsDashboard/RunnitBoardRow.vue';
import TableBulkActionsPopover from '@/components/base/TableBulkActionsPopover.vue';
import DebouncedInput from '@/components/base/DebouncedInput.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import {
	getRunnitsBoardSearchQuery,
	setRunnitsBoardSearchQuery,
	getRunnitsBoardViewMode,
	setRunnitsBoardViewMode,
} from '@/utils/localStorage';
import { BoardViewMode, BoardsFilter } from '@/views/Runnits/pages/RunnitBoardsPage/types';
import ViewModeToggle from '@/views/Runnits/pages/RunnitBoardsPage/components/ViewModeToggle.vue';
import RunnitBoardsFilterMenu from '@/views/Runnits/pages/RunnitBoardsPage/components/RunnitBoardsFilterMenu.vue';
import LoadingState from '@/components/states/LoadingState/LoadingState.vue';
import RunnitTemplateTile from './RunnitTemplateTile.vue';
import { ROUTER } from '@/router/constants';
import BackButton from '@/components/base/BackButton.vue';
import TeamMembersPicker from '@/components/TeamMembersPicker.vue';

export default Vue.extend({
	name: 'RunnitTemplatesManagement',
	mixins: [
		RunnitsCRUDMixin,
		TeamUserMixin,
	],
	props: {
		allRunnits: { type: Array, default: () => [] },
		loadingAllRunnits: { type: Boolean, default: false },
		templatesFilter: { type: String, default: BoardsFilter.ALL },
	},
	data () {
		const DEFAULT_PAGINATION = {
			sortBy: ['createdAt'],
			sortDesc: [true], // newest first
		};
		return {
			RUNNITS_OWNER_SELECTION,
			BOARDS_FILTER: BoardsFilter,
			didHydrateLastPagination: false,
			DEFAULT_PAGINATION,
			pagination: DEFAULT_PAGINATION,
			itemsPerPageOptions: [25, 100],
			selectedRows: [],
			searchQuery: getRunnitsBoardSearchQuery() || '',
			viewMode: (getRunnitsBoardViewMode() as BoardViewMode) || BoardViewMode.TABLE,
			filterMenuOpen: false,
			mouseX: 0,
			mouseY: 0,
			contextMenu: {
				open: false,
				x: 0,
				y: 0,
				runnit: null,
			},
			selectedTeamMembers: [] as TeamMember[],
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
			'runnitState',
			'runnitBoardDashboard',
		]),
		runnitCustomFieldDefs (): CompanyCustomFieldDef[] {
			return this.team?.company?.runnitCustomFieldDefs || [];
		},
		// Filtered runnits by search query using fuzzy search
		filteredRunnits () {
			let runnits = this.allRunnits || [];

			// First, filter by selected users if any
			if (this.selectedTeamMembers.length > 0) {
				const selectedMap: Record<string, TeamMember> = _mapKeys(
					this.selectedTeamMembers.filter((member: TeamMember) => member.userId),
					'userId',
				);
				runnits = runnits.filter((runnit: Runnit) => !runnit.isFeatured && selectedMap[runnit.userId]);
			}

			// Then apply search filter
			if (!this.searchQuery.trim()) {
				return runnits;
			}

			const searchFields = [
				{ name: 'title', weight: 0.5 },
				{ name: 'description', weight: 0.2 },
			];

			// Only include custom fields in search if in team mode
			if (this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM) {
				searchFields.push(
					...this.runnitCustomFieldDefs.map((fieldDef: CompanyCustomFieldDef) => ({
						name: `customFields.${fieldDef.key}`,
						weight: 0.2,
					}))
				);
			}

			return this.fuzzyFilter(
				runnits,
				this.searchQuery,
				searchFields,
			);
		},
		// Search results text for display
		searchResultsText (): string {
			const totalRunnits: number = (this.allRunnits || []).length;
			const filteredCount: number = this.filteredRunnits.length;

			if (this.searchQuery) {
				return `${filteredCount} of ${totalRunnits} ${pluralize('template', totalRunnits)} found`;
			}

			return null;
		},
		headers () {
			return [
				{
					text: 'Name',
					value: 'title',
					align: 'left',
					class: 'py-0 pl-0 pr-1',
					sortable: true,
				},
				{
					text: 'Description',
					value: 'description',
					align: 'left',
					class: 'py-0 px-1',
					sortable: true,
				},
				...(this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM ? this.runnitCustomFieldDefs.map((fieldDef: CompanyCustomFieldDef) => ({
					text: fieldDef.label,
					value: `customFields.${fieldDef.key}`,
					align: 'left',
					class: 'py-0 px-1',
					sortable: true,
				})) : []),
				{
					noDownload: true,
					text: 'Created',
					value: 'createdAt',
					align: 'left',
					class: 'py-0 px-1',
					sortable: true,
				},
				...(this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM ? [
					{
						noDownload: true,
						text: 'Owner',
						value: 'userId',
						align: 'left',
						class: 'py-0 px-1',
						sortable: false,
					},
				] : []),
				...(this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM ? [
					{
						noDownload: true,
						text: 'Access',
						align: 'left',
						class: 'py-0 px-1',
						sortable: false,
					},
				] : []),
				{
					noDownload: true,
					text: 'Actions',
					align: 'right',
					class: 'py-0 pr-4',
					sortable: false,
				},
			];
		},
		isGridView (): boolean {
			return this.viewMode === BoardViewMode.GRID;
		},
		isTableView (): boolean {
			return this.viewMode === BoardViewMode.TABLE;
		},
		contextMenuRunnitIsFeatured (): boolean {
			return !!(this.contextMenu.runnit && this.contextMenu.runnit.isFeatured);
		},
	},
	watch: {
		team: {
			immediate: true,
			handler (newVal: Team, oldVal: Team) {
				const oldId: string = oldVal?.id || null;
				const newId: string = newVal?.id || null;
				if (oldId !== newId) this.clearSelectedRows();

				this.hydrateLastPaginationOnData(this.allRunnits, newVal);
			},
		},
		filteredRunnits: {
			immediate: true,
			handler (newVal: Runnit[], oldVal: Runnit[]) {
				this.hydrateLastPaginationOnData(newVal, this.team);

				// Sync selectedRows with latest data
				if (newVal !== oldVal && this.selectedRows.length > 0) {
					const allRunnitsMap: Record<string, Runnit> = _mapKeys(newVal, 'id');
					this.selectedRows = this.selectedRows.map((runnit: Runnit) => allRunnitsMap[runnit.id] || runnit);
				}
			},
		},
	},
	beforeDestroy () {
		window.removeEventListener('mousemove', this.onMouseMove, { capture: false } as any);
		if (this.team?.id) {
			this.storeLastPagination({
				key: 'lastRunnitTemplatesPagination',
				pagination: this.pagination,
				teamId: this.team?.id,
			});
		}
	},
	mounted () {
		window.addEventListener('mousemove', this.onMouseMove, { passive: true } as any);
	},
	methods: {
		...mapActions([
			'storeLastPagination',
			'updateRunnitState',
			'updateSnackbar',
		]),
		onNewRunnitClick () {
			this.updateRunnitState({
				runnit: null,
				createRunnitTemplateOpen: true,
			});
		},
		onMouseMove (e: MouseEvent) {
			this.mouseX = e.clientX;
			this.mouseY = e.clientY;
		},
		openContextMenu (runnit: Runnit) {
			this.contextMenu.runnit = runnit;
			this.contextMenu.x = this.mouseX;
			this.contextMenu.y = this.mouseY;
			this.contextMenu.open = true;
		},
		onRowOpenMenu (runnit: Runnit) {
			this.openContextMenu(runnit);
		},
		onTileClick (runnit: Runnit) {
			this.openContextMenu(runnit);
		},
		setViewMode (mode: BoardViewMode) {
			try {
				this.viewMode = mode;
				setRunnitsBoardViewMode(mode);
			} catch (error) {
				console.warn('Failed to save view mode preference:', error);
				this.viewMode = mode;
			}
		},
		onFilterChange (filter: BoardsFilter) {
			this.$emit('update:templates-filter', filter);
			this.filterMenuOpen = false;
		},
		clearSelectedRows () {
			this.$nextTick(() => {
				// Next tick seems to prevent issue with check boxes getting in a state where they're unselectable after a bulk update and clear selected items operation
				this.selectedRows = [];
			});
		},
		hydrateLastPaginationOnData (tableData: Runnit[], team: Team) {
			if (
				!this.didHydrateLastPagination &&
				tableData?.length &&
				team &&
				this.runnitBoardDashboard?.lastRunnitTemplatesPagination?.[team.id]
			) {
				this.didHydrateLastPagination = true;
				setTimeout(() => {
					// setTimeout to allow tableData to render first
					this.pagination = this.runnitBoardDashboard.lastRunnitTemplatesPagination[team.id];
				}, 400);
			}
		},
		onEditRunnit (runnit: Runnit) {
			this.$emit('edit-runnit', runnit);
		},
		onOpenRunnit (runnit: Runnit) {
			this.$emit('open-runnit', runnit);
		},
		onDeleteRunnit (runnit: Runnit) {
			this.$emit('delete-runnit', runnit);
		},
		setSearchQuery (query: string) {
			this.searchQuery = query || '';

			// Persist to localStorage
			setRunnitsBoardSearchQuery(query || '');

			// Reset pagination to first page when searching
			this.pagination = {
				...this.pagination,
				page: 1,
			};
		},
		onTeamMembersSelect (selectedMembers: TeamMember[]) {
			this.selectedTeamMembers = selectedMembers;

			// Reset pagination to first page when filtering
			this.pagination = {
				...this.pagination,
				page: 1,
			};
		},
		fuzzyFilter (items: Runnit[], searchQuery: string, fields: { name: string; weight: number }[]) {
			if (!searchQuery.trim()) return items;

			const query: string = searchQuery.toLowerCase();

			return items.filter((item: Runnit) => {
				return fields.some(field => {
					const value: any = this._get(item, field.name);
					return value && value.toString().toLowerCase().includes(query);
				});
			});
		},
		onViewPreview () {
			if (this.contextMenu?.runnit) {
				this.$emit('open-runnit', this.contextMenu.runnit);
			}
			this.contextMenu.open = false;
		},
		onCreateBoard () {
			const runnit: any = this.contextMenu?.runnit;
			if (!runnit) {
				this.contextMenu.open = false;
				return;
			}
			// Check if user can create a new runnit (this will show upsell dialog if limit reached)
			if (!this.canCreateNewRunnit(false)) {
				this.contextMenu.open = false;
				return;
			}
			// Mirror ManageRunnitMenuBtn CREATE_BOARD behavior: open ConfigureRunnit with cloned draft
			const clonedRunnit: any = {
				...runnit,
				title: `${runnit.title} (Copy)`,
				createdAt: new Date(),
				deletedAt: null,
				type: 'WORKFLOW',
				accessLevel: 'PRIVATE',
				teamId: this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM ? this.team?.id : null,
			};
			delete clonedRunnit.id;
			this.updateRunnitState({
				runnitDraft: clonedRunnit,
				runnitDraftNodeRef: null,
				configureRunnitOpen: true,
				cloneFromTemplate: true,
				templateRunnit: runnit,
			});
			this.contextMenu.open = false;
		},
		goBack () {
			this.routerPush(this.$route, this.$router, { name: ROUTER.RUNNIT_BOARDS_PAGE, query: this.$route.query });
		},
		async onFeatureTemplate (isFeatured: boolean) {
			if (!this.contextMenu.runnit || !this.user?.isAdmin) return;

			const runnit = this.contextMenu.runnit as Runnit;

			try {
				await db.collection('runnits').doc(runnit.id).update({
					isFeatured,
				});
				this.updateSnackbar({
					message: isFeatured ? 'Template featured successfully' : 'Template unfeatured successfully',
					status: 'success',
				});
			} catch (error) {
				console.error('Error updating featured status:', error);
				this.updateSnackbar({
					message: 'Failed to update featured status',
					status: 'error',
				});
			} finally {
				this.contextMenu.open = false;
			}
		},
	},
	components: {
		RunnitBoardRow,
		TableBulkActionsPopover,
		DebouncedInput,
		ViewModeToggle,
		RunnitBoardsFilterMenu,
		LoadingState,
		BaseButton,
		RunnitTemplateTile,
		BackButton,
		TeamMembersPicker,
	},
});
